import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { placeOrder, makePayment, getSiteSettings } from '../services/api';
import './Checkout.css';

export default function Checkout() {
  const { user } = useAuth();
  const { cart, clearCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  /* ─── Buy Now detection ─── */
  const isBuyNow = location.state?.buyNow === true;
  const buyNowItem = location.state?.item || null;

  /* ─── Items source ─── */
  const items = useMemo(() => {
    if (isBuyNow && buyNowItem) return [buyNowItem];
    return cart.map((i) => ({
      product_id: i.id || i.product_id,
      id: i.id || i.product_id,
      name: i.name,
      price: Number(i.price),
      old_price: i.old_price ? Number(i.old_price) : null,
      image: i.image || i.image_url,
      slug: i.slug,
      tax_percentage: Number(i.tax_percentage || 0),
      quantity: i.quantity,
    }));
  }, [isBuyNow, buyNowItem, cart]);

  /* ─── Settings from admin ─── */
  const [settings, setSettings] = useState({
    delivery_charge: 50,
    free_delivery_above: 999,
    handling_fee: 20,
    enable_delivery_charge: true,
    enable_handling_fee: true,
  });

  useEffect(() => {
    getSiteSettings()
      .then((res) => setSettings((prev) => ({ ...prev, ...res.data })))
      .catch(() => { /* keep defaults */ });
  }, []);

  /* ─── Redirect if nothing to checkout ─── */
  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: '/checkout' } } });
      return;
    }
    if (items.length === 0) {
      navigate('/cart');
    }
  }, [user, items.length, navigate]);

  /* ─── Calculations ─── */
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const taxAmount = items.reduce(
    (s, i) => s + (i.price * i.quantity * (i.tax_percentage || 0)) / 100,
    0
  );

  const deliveryCharge =
    settings.enable_delivery_charge && subtotal < Number(settings.free_delivery_above)
      ? Number(settings.delivery_charge)
      : 0;

  const handlingFee = settings.enable_handling_fee
    ? Number(settings.handling_fee)
    : 0;

  const grandTotal = subtotal + taxAmount + deliveryCharge + handlingFee;

  /* ─── Form state ─── */
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [address, setAddress] = useState({
    full_name: user?.username || '',
    phone: '',
    address: '',
    city: '',
    pincode: '',
  });

  // ✅ COD only — other methods will be added later
  const [paymentMethod, setPaymentMethod] = useState('cod');

  const formatPrice = (v) =>
    new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(v);

  /* ─── Place Order ─── */
  const handlePlaceOrder = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        ...address,
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
        })),
      };

      const orderRes = await placeOrder(payload);
      const orderId = orderRes.data.order_id;

      // ✅ COD only — payment gateway integration will be added later
      try {
        await makePayment({ order_id: orderId, method: paymentMethod });
      } catch {
        /* payment optional */
      }

      if (!isBuyNow) {
        clearCart();
      }

      navigate('/order-success', {
        state: {
          orderId,
          email: user?.email,
          totalAmount: orderRes.data.total_amount,
        },
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Order failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    display: 'block',
    width: '100%',
    padding: '12px 14px',
    border: '1.5px solid #d8d2c4',
    borderRadius: 10,
    fontSize: 14,
    marginBottom: 14,
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    background: '#faf9f6',
    outline: 'none',
  };

  return (
    <>
      <Navbar />
      <div className="ck-page">
        <div className="ck-container">

          <div className="ck-header">
            <Link to={isBuyNow ? '/' : '/cart'} className="ck-back">
              ← Back
            </Link>
            <h1 className="ck-title">
              {isBuyNow ? 'Buy Now' : 'Checkout'}
            </h1>
            {isBuyNow && (
              <p className="ck-badge">Single item checkout</p>
            )}
          </div>

          <div className="ck-steps">
            {['Address', 'Payment'].map((s, i) => (
              <div
                key={i}
                className={`ck-step ${step === i + 1 ? 'active' : ''} ${
                  step > i + 1 ? 'done' : ''
                }`}
              >
                <span className="ck-step-num">{i + 1}</span>
                {s}
              </div>
            ))}
          </div>

          {error && <div className="ck-error">{error}</div>}

          <div className="ck-layout">
            <div className="ck-main">
              {/* Step 1 — Address */}
              {step === 1 && (
                <div className="ck-card">
                  <h2 className="ck-card-title">📍 Shipping Address</h2>

                  <input
                    placeholder="Full name *"
                    value={address.full_name}
                    onChange={(e) =>
                      setAddress({ ...address, full_name: e.target.value })
                    }
                    style={inputStyle}
                  />
                  <input
                    placeholder="Phone number *"
                    value={address.phone}
                    onChange={(e) =>
                      setAddress({ ...address, phone: e.target.value })
                    }
                    style={inputStyle}
                  />
                  <textarea
                    placeholder="Full address *"
                    value={address.address}
                    onChange={(e) =>
                      setAddress({ ...address, address: e.target.value })
                    }
                    style={{ ...inputStyle, height: 90, resize: 'vertical' }}
                  />
                  <div className="ck-row">
                    <input
                      placeholder="City *"
                      value={address.city}
                      onChange={(e) =>
                        setAddress({ ...address, city: e.target.value })
                      }
                      style={{ ...inputStyle, flex: 2, marginBottom: 0 }}
                    />
                    <input
                      placeholder="Pincode *"
                      value={address.pincode}
                      onChange={(e) =>
                        setAddress({ ...address, pincode: e.target.value })
                      }
                      style={{ ...inputStyle, flex: 1, marginBottom: 0 }}
                    />
                  </div>

                  <button
                    className="ck-btn-primary"
                    onClick={() => {
                      if (
                        !address.full_name ||
                        !address.phone ||
                        !address.address ||
                        !address.city ||
                        !address.pincode
                      ) {
                        setError('Please fill all address fields.');
                        return;
                      }
                      setError('');
                      setStep(2);
                    }}
                  >
                    Continue to Payment →
                  </button>
                </div>
              )}

              {/* Step 2 — Payment */}
              {step === 2 && (
                <div className="ck-card">
                  <h2 className="ck-card-title">💳 Payment Method</h2>

                  {[
                    { value: 'cod', label: '💵 Cash on Delivery', desc: 'Pay when delivered' },
                    // ─── TODO: Add this later ───
                    // { value: 'upi',  label: '📱 UPI',                desc: 'Google Pay, PhonePe, Paytm' },
                    // { value: 'card', label: '💳 Credit / Debit Card', desc: 'Visa, Mastercard, RuPay' },
                  ].map((m) => (
                    <label
                      key={m.value}
                      className={`ck-pay ${paymentMethod === m.value ? 'active' : ''}`}
                    >
                      <input
                        type="radio"
                        name="payment"
                        value={m.value}
                        checked={paymentMethod === m.value}
                        onChange={() => setPaymentMethod(m.value)}
                      />
                      <div className="ck-pay-text">
                        <div className="ck-pay-label">{m.label}</div>
                        <div className="ck-pay-desc">{m.desc}</div>
                      </div>
                    </label>
                  ))}

                  <div className="ck-buttons">
                    <button className="ck-btn-ghost" onClick={() => setStep(1)}>
                      ← Back
                    </button>
                    <button
                      className="ck-btn-primary ck-btn-submit"
                      onClick={handlePlaceOrder}
                      disabled={loading}
                    >
                      {loading ? 'Placing order…' : '✅ Place Order'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <aside className="ck-side">
              <div className="ck-card">
                <h3 className="ck-card-title">Order Summary</h3>

                <div className="ck-items">
                  {items.map((i) => (
                    <div key={i.product_id} className="ck-item">
                      <div className="ck-item-info">
                        <span className="ck-item-name">{i.name}</span>
                        <span className="ck-item-meta">
                          ₹{formatPrice(i.price)} × {i.quantity}
                        </span>
                      </div>
                      <span className="ck-item-total">
                        ₹{formatPrice(i.price * i.quantity)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="ck-divider" />

                <div className="ck-line">
                  <span>Subtotal</span>
                  <span>₹{formatPrice(subtotal)}</span>
                </div>

                <div className="ck-line">
                  <span>Tax</span>
                  <span>₹{formatPrice(taxAmount)}</span>
                </div>

                <div className="ck-line">
                  <span>Delivery</span>
                  {deliveryCharge > 0 ? (
                    <span>₹{formatPrice(deliveryCharge)}</span>
                  ) : (
                    <span className="ck-free">FREE</span>
                  )}
                </div>

                <div className="ck-line">
                  <span>Handling</span>
                  <span>₹{formatPrice(handlingFee)}</span>
                </div>

                <div className="ck-divider" />

                <div className="ck-total">
                  <span>Total</span>
                  <span className="ck-total-amount">
                    ₹{formatPrice(grandTotal)}
                  </span>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
}